import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Form from 'react-bootstrap/Form';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { memberHandler, masterHandler } from '../../../redux/handlers';

export function NormalPerson({
  setFieldValue,
  values,
  handleChange,
  errors,
  touched,
  identitycard,
  handleinputTab,
  prefixNames
}) {
  const { businessSlug } = useParams();
  const dispatch = useDispatch();

  const genders = useSelector(state => state.master.genders || []);

  const [startDate, setStartDate] = useState('');
  const [memberGroups, setMemberGroups] = useState([]);
  const [loading, setLoading] = useState(false);  

  const prefixOptions =
    prefixNames?.map(item => ({
      value: item.value,
      label: item.label
    })) || [];

  const genderOptions = genders.map(item => ({
    value: item.id,
    label: item.name
  }));

  useEffect(() => {
    const fetchMemberGroups = async () => {
      if (!businessSlug) return;

      try {
        setLoading(true);
        const response = await dispatch(memberHandler.getAllMemberGroups(businessSlug));

        if (response) {
          const formattedGroups = response.map(group => ({
            value: group.id,
            label: group.name
          }));
          setMemberGroups(formattedGroups);
        }
      } catch (error) {
        console.error('Error fetching member groups:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMemberGroups();
  }, [dispatch, businessSlug]);

  useEffect(() => {
    const fetchGenders = async () => {
      try {
        await dispatch(masterHandler.getGenders());
      } catch (error) {
        console.error('Error fetching genders:', error);
      }
    };

    if (genders.length === 0) {
      fetchGenders();
    }
  }, [dispatch, genders.length]);

  useEffect(() => {
    if (values.birthday) {
      const parsedDate = moment(values.birthday, 'DD/MM/YYYY', true);
      if (parsedDate.isValid()) {
        setStartDate(parsedDate.toDate());
        if (!values.age) {
          const calculatedAge = calculateAge(values.birthday);
          if (calculatedAge) {
            setFieldValue('age', calculatedAge);
          }
        }
      }
    } else {
      setStartDate(null);
    }
  }, [values.birthday, setFieldValue]);

  const calculateAge = birthDate => {
    if (!birthDate) return '';

    const today = moment();
    const birth = moment(birthDate, 'DD/MM/YYYY', true);

    if (!birth.isValid()) return '';

    const age = today.diff(birth, 'years');
    return age >= 0 ? age.toString() : '';
  };

  const getCurrentDate = (separator = '', dateb = new Date()) => {
    return moment(dateb).format(`DD${separator}MM${separator}YYYY`);
  };

  return (
    <div className="px-3">
      <div className="row gx-xl-4">
        <div className="col-xl-3 col-md-6">
          <Form.Group className="mb-3 form-st-3 pb-1">
            <Form.Label className="mb-1">
              รหัสลูกค้า <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="code"
              className="form-control"
              placeholder="รหัสลูกค้า"
              value={values.code}
              onChange={handleChange}
              isInvalid={touched.code && !!errors.code}
              disabled={true}
            />
            <Form.Control.Feedback type="invalid">{errors.code}</Form.Control.Feedback>
          </Form.Group>
        </div>

        {prefixOptions.length > 0 && values.entity_structure_id && (
          <div className="col-xl-3 col-md-6">
            <Form.Group className="mb-3 form-st-3 pb-1">
              <Form.Label className="mb-1">คำนำหน้า</Form.Label>
              <Select
                name="prefix_name_id"
                onChange={selectedOption =>
                  setFieldValue('prefix_name_id', selectedOption ? selectedOption.value : null)
                }
                className={`w-100 select-st-1 ${touched.prefix_name_id && errors.prefix_name_id ? 'is-invalid' : ''}`}
                options={prefixOptions}
                placeholder="เลือกคำนำหน้า"
                value={prefixOptions.find(item => item.value === values.prefix_name_id)}
                isDisabled={!(prefixOptions.length > 0 && values.entity_structure_id)}
                isClearable
              />
              {touched.prefix_name_id && errors.prefix_name_id && (
                <div className="invalid-feedback d-block">{errors.prefix_name_id}</div>
              )}
            </Form.Group>
          </div>
        )}

        <div className="col-xl-3 col-md-6">
          <Form.Group className="mb-3 form-st-3 pb-1">
            <Form.Label className="mb-1">
              ชื่อ (TH) <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="first_name_th"
              className="form-control"
              placeholder="ชื่อภาษาไทย"
              value={values.first_name_th}
              onChange={handleChange}
              isInvalid={touched.first_name_th && !!errors.first_name_th}
            />
            <Form.Control.Feedback type="invalid">{errors.first_name_th}</Form.Control.Feedback>
          </Form.Group>
        </div>

        <div className="col-xl-3 col-md-6">
          <Form.Group className="mb-3 form-st-3 pb-1">
            <Form.Label className="mb-1">
              นามสกุล (TH) <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="last_name_th"
              className="form-control"
              placeholder="นามสกุลภาษาไทย"
              value={values.last_name_th}
              onChange={handleChange}
              isInvalid={touched.last_name_th && !!errors.last_name_th}
            />
            <Form.Control.Feedback type="invalid">{errors.last_name_th}</Form.Control.Feedback>
          </Form.Group>
        </div>

        <div className="col-xl-3 col-md-6">
          <Form.Group className="mb-3 form-st-3 pb-1">
            <Form.Label className="mb-1">ชื่อ (EN)</Form.Label>
            <Form.Control
              type="text"
              name="first_name_en"
              className="form-control"
              placeholder="ชื่อภาษาอังกฤษ"
              value={values.first_name_en}
              onChange={handleChange}
              isInvalid={touched.first_name_en && !!errors.first_name_en}
            />
            <Form.Control.Feedback type="invalid">{errors.first_name_en}</Form.Control.Feedback>
          </Form.Group>
        </div>

        <div className="col-xl-3 col-md-6">
          <Form.Group className="mb-3 form-st-3 pb-1">
            <Form.Label className="mb-1">นามสกุล (EN)</Form.Label>
            <Form.Control
              type="text"
              name="last_name_en"
              className="form-control"
              placeholder="นามสกุลภาษาอังกฤษ"
              value={values.last_name_en}
              onChange={handleChange}
              isInvalid={touched.last_name_en && !!errors.last_name_en}
            />
            <Form.Control.Feedback type="invalid">{touched.last_name_en && errors.last_name_en}</Form.Control.Feedback>
          </Form.Group>
        </div>

        <div className="col-xl-6 col-md-12">
          <Form.Label className="mb-1 tc-10 fs-12">หมายเลขบัตรประชาชน (13 หลัก)</Form.Label>
          <div className="my-0 form-st-3 identitycard">
            <div className="d-flex gap-1">
              {Array.from({ length: 13 }, (_, index) => (
                <Form.Control
                  key={index}
                  type="text"
                  maxLength="1"
                  className="form-control text-center"
                  style={{ width: '40px' }}
                  value={identitycard[index] || ''}
                  onChange={e => handleinputTab(e, index)}
                  onKeyDown={e => {
                    if (e.key === 'Backspace' && !e.target.value && index > 0) {
                      const prevInput = document.querySelector(`input[name="identitycard_${index - 1}"]`);
                      if (prevInput) prevInput.focus();
                    } else if (e.target.value && index < 12 && e.key !== 'Backspace') {
                      const nextInput = document.querySelector(`input[name="identitycard_${index + 1}"]`);
                      if (nextInput) nextInput.focus();
                    }
                  }}
                  name={`identitycard_${index}`}
                />
              ))}
            </div>
          </div>
          {touched.tax_number && errors.tax_number && (
            <div className="invalid-feedback d-block">{errors.tax_number}</div>
          )}
        </div>

        <div className="col-xl-3 col-md-6">
          <Form.Group className="mb-3 form-st-3 pb-1">
            <Form.Label className="mb-1">เพศ</Form.Label>
            <Select
              name="gender_id"
              onChange={selectedOption => setFieldValue('gender_id', selectedOption ? selectedOption.value : null)}
              className={`w-100 select-st-1 ${touched.gender_id && errors.gender_id ? 'is-invalid' : ''}`}
              options={genderOptions}
              placeholder="เลือกเพศ"
              value={genderOptions.find(item => item.value === values.gender_id)}
              isClearable
            />
            {touched.gender_id && errors.gender_id && (
              <div className="invalid-feedback d-block">{errors.gender_id}</div>
            )}
          </Form.Group>
        </div>

        <div className="col-xl-3 col-md-6">
          <Form.Group className="mb-3 form-st-3 pb-1">
            <Form.Label className="mb-1">วันเกิด</Form.Label>
            <DatePicker
              selected={startDate}
              onChange={date => {
                setStartDate(date);
                if (date) {
                  const formattedDate = getCurrentDate('/', date);
                  setFieldValue('birthday', formattedDate);

                  const isoDate = moment(date).format('YYYY-MM-DD');
                  setFieldValue('birth_date', isoDate);

                  const calculatedAge = calculateAge(formattedDate);
                  setFieldValue('age', calculatedAge);
                } else {
                  setFieldValue('birthday', '');
                  setFieldValue('birth_date', '');
                  setFieldValue('age', '');
                }
              }}
              dateFormat="dd/MM/yyyy"
              placeholderText="DD/MM/YYYY"
              className={`w-100 datepicker2 form-control ${touched.birthday && errors.birthday ? 'is-invalid' : ''}`}
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
              yearDropdownItemNumber={100}
              scrollableYearDropdown
              maxDate={new Date()}
            />
            {touched.birthday && errors.birthday && <div className="invalid-feedback d-block">{errors.birthday}</div>}
          </Form.Group>
        </div>

        <div className="col-xl-3 col-md-6">
          <Form.Group className="mb-3 form-st-3 pb-1">
            <Form.Label className="mb-1">อายุ</Form.Label>
            <Form.Control
              type="number"
              name="age"
              className="form-control"
              placeholder="อายุ"
              value={values.age}
              onChange={handleChange}
              isInvalid={touched.age && !!errors.age}
              readOnly
              style={{ backgroundColor: '#f8f9fa' }}
            />
            <Form.Control.Feedback type="invalid">{errors.age}</Form.Control.Feedback>
          </Form.Group>
        </div>

        <div className="col-xl-3 col-md-6">
          <Form.Group className="mb-3 form-st-3 pb-1">
            <Form.Label className="mb-1">กลุ่มสมาชิก</Form.Label>
            <Select
              name="member_group_id"
              onChange={selectedOption =>
                setFieldValue('member_group_id', selectedOption ? selectedOption.value : null)
              }
              className={`w-100 select-st-1 ${touched.member_group_id && errors.member_group_id ? 'is-invalid' : ''}`}
              options={memberGroups}
              placeholder={loading ? 'กำลังโหลด...' : 'เลือกกลุ่มสมาชิก'}
              isLoading={loading}
              value={memberGroups.find(item => item.value === values.member_group_id)}
              isDisabled={loading}
              isClearable
            />
            {touched.member_group_id && errors.member_group_id && (
              <div className="invalid-feedback d-block">{errors.member_group_id}</div>
            )}
          </Form.Group>
        </div>

        <div className="col-xl-3 col-md-6">
          <Form.Group className="mb-3 form-st-3 pb-1">
            <Form.Label className="mb-1">
              เบอร์โทรศัพท์ <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="phone_number"
              className="form-control"
              placeholder="เบอร์โทรศัพท์"
              value={values.phone_number}
              onChange={handleChange}
              isInvalid={touched.phone_number && !!errors.phone_number}
            />
            <Form.Control.Feedback type="invalid">{errors.phone_number}</Form.Control.Feedback>
          </Form.Group>
        </div>

        <div className="col-xl-3 col-md-6">
          <Form.Group className="mb-3 form-st-3 pb-1">
            <Form.Label className="mb-1">E-mail</Form.Label>
            <Form.Control
              type="email"
              name="email"
              className="form-control"
              placeholder="อีเมล"
              value={values.email}
              onChange={handleChange}
              isInvalid={touched.email && !!errors.email}
            />
            <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
          </Form.Group>
        </div>

        <div className="col-xl-3 col-md-6">
          <Form.Group className="mb-3 form-st-3 pb-1">
            <Form.Label className="mb-1">คะแนนสะสม</Form.Label>
            <Form.Control
              type="number"
              name="totalPoint"
              className="form-control"
              placeholder="คะแนนสะสม"
              value={values.totalPoint}
              onChange={handleChange}
              isInvalid={touched.totalPoint && !!errors.totalPoint}
              disabled={true}
            />
            <Form.Control.Feedback type="invalid">{errors.totalPoint}</Form.Control.Feedback>
          </Form.Group>
        </div>

        <div className="col-xl-3 col-md-6">
          <Form.Group className="mb-3 form-st-3 pb-1">
            <Form.Label className="mb-1">LINE UID</Form.Label>
            <Form.Control
              type="text"
              name="line_uid"
              className="form-control"
              placeholder="LINE UID"
              value={values.line_uid ? 'เชื่อมต่อแล้ว' : '-'}
              onChange={handleChange}
              isInvalid={touched.line_uid && !!errors.line_uid}
              disabled={true}
              style={values.line_uid ? { color: '#2DC76D', fontSize: '14px' } : {}}
            />
            <Form.Control.Feedback type="invalid">{errors.line_uid}</Form.Control.Feedback>
          </Form.Group>
        </div>

        <div className="col-xl-3 col-md-6">
          <Form.Group className="mb-3 form-st-3 pb-1">
            <Form.Label className="mb-1">Facebook</Form.Label>
            <Form.Control
              type="text"
              name="facebook"
              className="form-control"
              placeholder="Facebook"
              value={values.facebook ? 'เชื่อมต่อแล้ว' : '-'}
              onChange={handleChange}
              isInvalid={touched.facebook && !!errors.facebook}
              disabled={true}
              style={values.facebook ? { color: '#2DC76D', fontSize: '14px' } : {}}
            />
            <Form.Control.Feedback type="invalid">{errors.facebook}</Form.Control.Feedback>
          </Form.Group>
        </div>
      </div>
    </div>
  );
}
